import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon } from "lucide-react";
import { useTheme } from "next-themes";
import { Button } from "./primitives";

const links = [
  { to: "/about", label: "About" },
  { to: "/values", label: "Our Values" },
  { to: "/services", label: "Services" },
  { to: "/manifesto", label: "Manifesto" },
  { to: "/support", label: "Support" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-cream/80 border-b border-black/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="font-serif text-2xl tracking-tight text-ink">
          Glam <span className="italic text-gold">Wagon</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-opacity duration-300 hover:opacity-60 ${
                  isActive ? "text-gold" : "text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="p-2 rounded-full hover:bg-surface transition-colors text-ink"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <Button as={Link} to="/book" data-testid="nav-book-btn" className="hidden sm:inline-flex">
            Book Appointment
          </Button>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            className="lg:hidden p-2 text-ink"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-cream/95 border-b border-line"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {[...links, { to: "/constitution", label: "Constitution" }, { to: "/faq", label: "FAQ" }, { to: "/contact", label: "Contact" }].map(
                (l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="font-serif text-2xl text-ink"
                  >
                    {l.label}
                  </NavLink>
                )
              )}
              <Button as={Link} to="/book" data-testid="mobile-book-btn" className="mt-2 self-start">
                Book Appointment
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
